'use client';
import classNames from 'classnames';
import React from 'react';
import { useClient } from '../../hooks/useClient';
import { useAppendQueryParams } from '../../utils/useAppendQueryParams';
import type { FilterCategory } from './productFilterTypes';

export type FilterChipsGroupProps = {
  category: FilterCategory;
  /**
   * A chip was activated, for analytics. Not fired for the current page's chip,
   * which is inert (R4.5.4).
   */
  onActivate?: (chip: { label: string; url: string }) => void;
};

/**
 * A `displayMode: 'CHIPS'` group. These are **navigation**, not filters
 * (CMS-1893 R3.2, CMS-1542 R4.5): each chip links to a category results page,
 * so none of them narrows the list, enters the selection, or is touched by
 * Clear All.
 *
 * Real anchors rather than click handlers, so the category pages keep their
 * link equity — the prototype wires these to the filter, which R4.5 overrides.
 */
export const FilterChipsGroup: React.FC<FilterChipsGroupProps> = ({
  category,
  onActivate,
}) => {
  // The current path decides which chip is the page you are on. Read after
  // hydration for the same reason the sort is: the server cannot know it
  // without dropping the static cache.
  const pathname = useClient(() => window.location.pathname);

  // R4.5.3: the reader arrives with their search intact. The trip parameters
  // are on the URL of the page this panel is rendered on, and this hook already
  // forwards them — it skips `cms_*`, so the reader's own filters and sort do
  // not leak onto the destination (which R4.5.5 wants anyway), and it leaves an
  // external URL untouched. On the server it returns the editor's clean path,
  // so that is what gets crawled (R4.5.2).
  const appendQueryParams = useAppendQueryParams();

  if (!category.chips.length) return null;

  return (
    <div className="flex flex-col gap-3 p-4" data-component="FilterChipsGroup">
      {/* No collapse control: a chips group is not collapsible (R4.5.1), so
          this is a heading rather than the button a checkbox group uses. */}
      <p className="mhg-alpha-typography-body-1 font-bold text-text/primary">
        {category.name}
      </p>

      <div className="flex flex-col items-start gap-2.5">
        {category.chips.map((chip) => {
          const isCurrent =
            !!pathname && stripTrailingSlash(pathname) === toPath(chip.url);

          // The chip for this page is inert and announced as current, rather
          // than a link back to where the reader already is (R4.5.4).
          return isCurrent ? (
            <span
              key={chip.url}
              aria-current="page"
              className={classNames(
                CHIP,
                'border-fill/primary bg-fill/primary-light text-fill/primary-dark',
              )}
            >
              {chip.label}
            </span>
          ) : (
            <a
              key={chip.url}
              href={appendQueryParams(chip.url)}
              onClick={() => onActivate?.(chip)}
              className={classNames(
                CHIP,
                'border-borders/default bg-fill/card text-text/primary hover:bg-fill/alternative-1',
              )}
            >
              {chip.label}
            </a>
          );
        })}
      </div>
    </div>
  );
};

const CHIP =
  'mhg-alpha-typography-body-2 inline-flex items-center rounded-full border-[1.5px] px-[18px] py-2.5 font-semibold';

const stripTrailingSlash = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, '') : path;

/**
 * The editor authors a clean path, but an absolute URL is accepted too, so the
 * comparison is made on the path in both cases.
 */
const toPath = (url: string) => {
  const path = url.startsWith('http')
    ? (url.split('/').slice(3).join('/') ?? '')
    : url;
  return stripTrailingSlash(path.startsWith('/') ? path : `/${path}`);
};
